import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Popconfirm, Result, Select, Spin } from "antd";

import * as api from "../controller";
import { money } from "../../Subscription/money";
import { taxLinesOf, taxLinesPhrase } from "../../Subscription/taxLines";
import { pollCheckout } from "../../Subscription/useHelcimReturn";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

const REFUSED = {
  no_owner: "This practice has no owner account to charge.",
  no_saved_card: "The owner has no saved card. They can choose a plan in the app instead.",
  already_subscribed: "This office already has a plan. Change it from the office's Billing page.",
  tier_unavailable: "That plan isn't available to this office.",
  not_practice_admin: "The owner doesn't administer this office, so the plan can't go on their card.",
  charge_required: "Choose a plan to charge.",
};

const OUTCOME = {
  active: { status: "success", title: "Paid — the plan is active", sub: "The receipt is on the office's Billing page." },
  declined: { status: "error", title: "The card was declined", sub: "Nothing was charged. The owner can choose a plan with another card in the app." },
  provider_error: { status: "error", title: "The payment provider didn't answer", sub: "Check the office's Billing page before trying again — the charge may still land." },
  needs_review: { status: "warning", title: "The charge needs a look", sub: "It's flagged for review; don't charge again until it's resolved." },
  timeout: { status: "info", title: "Still confirming the charge", sub: "It's running in the background — refresh the drawer in a minute." },
};

/**
 * CRM → Complete office setup: buy an owner-kept office a plan on the owner's
 * saved card. Quote first (price with any offer, tax, whose card), then an
 * explicit confirm, then the same checkout a customer uses — the charge runs in
 * the background and this polls it. There is no "pending" option any more: an
 * unpaid purchase only waits to be charged by the next card attach.
 */
export default function CompleteSetupModal({ open, onClose, practiceId, officeId, onDone }) {
  const [tiers, setTiers] = useState(null);
  const [tierId, setTierId] = useState(null);
  const [preview, setPreview] = useState(null);   // {ok, charge, owner, has_saved_card} | {ok:false, detail}
  const [phase, setPhase] = useState("choose");   // choose | charging | done
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTierId(null); setPreview(null); setPhase("choose"); setOutcome(null); setTiers(null);
    api.getSubscriptionTiers(officeId).then((r) => setTiers(ok(r) && Array.isArray(r.json) ? r.json : []));
  }, [open, officeId]);

  const choose = async (id) => {
    setTierId(id);
    setPreview(null);
    const r = await api.completeOfficeSetup(practiceId, officeId, { subscription_tier_id: id, preview: true });
    const sub = ok(r) && r.json && r.json.subscription;
    setPreview(sub || { ok: false, detail: (r.json && r.json.detail) || "error" });
  };

  const charge = async () => {
    setPhase("charging");
    const r = await api.completeOfficeSetup(practiceId, officeId, { subscription_tier_id: tierId, activate: true });
    const sub = ok(r) && r.json && r.json.subscription;
    if (!sub || !sub.ok) {
      setPhase("choose");
      setPreview({ ok: false, detail: (sub && sub.detail) || (r.json && r.json.detail) || "error" });
      return;
    }
    const states = await pollCheckout([officeId]);
    const state = (states && states[officeId]) || "timeout";
    setOutcome(OUTCOME[state] || OUTCOME.timeout);
    setPhase("done");
    if (onDone) onDone();
  };

  const tier = (tiers || []).find((t) => t.id === tierId);
  const q = preview && preview.ok ? preview.charge : null;
  const annual = tier && Number(tier.term_months) >= 12;
  const canCharge = !!(q && preview.has_saved_card);

  let body;
  if (phase === "done" && outcome) {
    body = <Result status={outcome.status} title={outcome.title} subTitle={outcome.sub} style={{ padding: "12px 0" }} />;
  } else if (phase === "charging") {
    body = (
      <div style={{ textAlign: "center", padding: "28px 0" }}>
        <Spin size="large" />
        <div style={{ marginTop: 14, color: "#6b6680" }}>Charging the owner&rsquo;s card…</div>
      </div>
    );
  } else {
    body = (
      <>
        <p style={{ color: "#6b6680", marginTop: 0 }}>
          Buy this office a plan on the practice owner&rsquo;s saved card. Offices run by an invited
          admin choose their own plan in the app — use this only for offices the owner keeps.
        </p>
        <span className="crm-field-label">Plan</span>
        <Select
          style={{ width: "100%" }}
          loading={tiers === null}
          placeholder="Choose a plan"
          value={tierId}
          onChange={choose}
          dropdownStyle={{ zIndex: 10060 }}
          options={(tiers || []).map((t) => ({
            value: t.id,
            label: `${t.title}${t.is_custom ? " · special" : ""} — $${money(t.charge ? t.charge.total : t.price)}${
              Number(t.term_months) >= 12 ? "/yr" : "/mo"}${t.charge && Number(t.charge.discount) > 0 ? " (offer)" : ""}`,
          }))}
        />
        {tierId && !preview ? <Spin style={{ display: "block", margin: "18px auto 0" }} /> : null}
        {preview && !preview.ok ? (
          <Alert style={{ marginTop: 14 }} type="warning" showIcon message={REFUSED[preview.detail] || "This plan can't be charged right now."} />
        ) : null}
        {q ? (
          <div className="ub-callout" style={{ margin: "14px 0 0" }}>
            <div className="ub-facts" style={{ padding: 0 }}>
              <div>
                <div className="ub-fact-label">Charged today</div>
                <div className="ub-fact-value" style={{ fontSize: 18, fontWeight: 700 }}>${money(q.total)}</div>
                <div className="ub-fact-note" style={{ color: "#9a96a8" }}>
                  {Number(q.discount) > 0 ? <span className="ub-strike">${money(q.list_subtotal)}</span> : null}
                  ${money(q.subtotal)}{taxLinesOf(q).length ? ` + ${taxLinesPhrase(taxLinesOf(q))}` : " (no sales tax)"}
                </div>
              </div>
              <div>
                <div className="ub-fact-label">Then</div>
                <div className="ub-fact-value">Every {annual ? "year" : "month"}, same price</div>
                {q.offer ? (
                  <div className="ub-fact-note">{q.offer.label}: −${money(q.discount)} before tax</div>
                ) : null}
              </div>
              <div>
                <div className="ub-fact-label">Card</div>
                <div className="ub-fact-value">{preview.has_saved_card ? `${preview.owner}'s saved card` : "None saved"}</div>
              </div>
            </div>
            {!preview.has_saved_card ? (
              <div style={{ marginTop: 10, color: "#8a5a00" }}>
                The owner has no saved card, so this can&rsquo;t be charged from here. They can choose a plan in the
                app (Billing → Choose a plan).
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  const footer = phase === "done" ? (
    <Button type="primary" onClick={onClose}>Done</Button>
  ) : phase === "charging" ? null : (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <Button onClick={onClose}>Cancel</Button>
      <Popconfirm
        title={q ? `Charge $${money(q.total)} to ${preview.owner}'s card now?` : ""}
        okText="Charge now"
        disabled={!canCharge}
        onConfirm={charge}
        zIndex={10060}
      >
        <Button type="primary" disabled={!canCharge}>
          {q ? `Charge $${money(q.total)}` : "Charge"}
        </Button>
      </Popconfirm>
    </div>
  );

  return (
    <Modal
      title="Complete office setup"
      open={open}
      onCancel={phase === "charging" ? undefined : onClose}
      closable={phase !== "charging"}
      maskClosable={phase !== "charging"}
      footer={footer}
      zIndex={10050}
      destroyOnClose
    >
      {body}
    </Modal>
  );
}
